<template>
  <div class="wallet-container">
    <v-expansion-panel popout light class="futuristic-expansion">
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            <h4 class="earth-color">USER WALLET OVERVIEW</h4>
            <div class="title-decoration"></div>
          </div>
        </template>
        <v-card flat class="futuristic-card">
          <v-container fluid>
            <v-layout row wrap>
              <v-flex lg6 md6 sm12 xs12>
                <v-autocomplete
                  label="Filter by Currency"
                  v-model="selectedCurrency"
                  :items="currencyOptions"
                  item-text="name"
                  item-value="public_id"
                  class="futuristic-input"
                ></v-autocomplete>
              </v-flex>
              <v-flex lg6 md6 sm12 xs12>
                <v-btn depressed @click="refreshBalance" color="#587e27" :loading="loading" class="futuristic-btn">
                  <v-icon left>refresh</v-icon>
                  REFRESH
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <!-- User Wallet Card -->
    <v-layout row wrap class="mb-4">
      <v-flex lg12 md12 sm12 xs12>
        <v-card class="futuristic-card balance-card unified-card" hover>
          <v-card-text>
            <div class="balance-icon">
              <v-icon large color="#587e27">account_balance_wallet</v-icon>
            </div>
            <div class="balance-label">{{ userInfo.full_name || 'User' }}'S WALLET BALANCE</div>
            <div class="balance-amount">
              {{ formatPrice(displayedBalance) }} 
              <span class="currency-symbol">{{ displayCurrencySign }}</span>
            </div>
            <div class="user-info">
              <v-chip small color="#587e27" text-color="white" class="mr-2">
                <v-icon small left>email</v-icon>
                {{ userInfo.email || 'No email' }}
              </v-chip>
              <v-chip small color="#2980b9" text-color="white">
                <v-icon small left>phone</v-icon>
                {{ userInfo.phone_number || 'No phone' }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card flat class="futuristic-card">
      <v-card-title class="card-header">
        <div>
          <h4 class="earth-color">WALLET TRANSACTIONS ({{ filteredTransactions.length }})</h4>
          <div class="title-decoration"></div>
        </div>
        <v-spacer></v-spacer>
        <v-text-field append-icon="search" label="Search" single-line hide-details v-model="search" class="futuristic-search"></v-text-field>
      </v-card-title>
      
      <v-data-table
        :headers="columns"
        :items="filteredTransactions"
        :search="search"
        :rows-per-page-items="[10, 25, 50, { text: 'All', value: -1 }]"
        :loading="transactionsLoading"
        class="futuristic-table"
      >
        <v-progress-linear slot="progress" color="#587e27" indeterminate></v-progress-linear>
        
        <template v-slot:items="props">
          <td data-label="Date">{{ formatDateTime(props.item.created_at) }}</td>
          <td data-label="Reference">{{ props.item.reference }}</td>
          <td data-label="Type">
            <span :class="getTransactionTypeClass(props.item.transaction_type)">
              {{ (props.item.transaction_type || 'N/A').toUpperCase() }}
            </span>
          </td>
          <td data-label="Amount" :class="getAmountClass(props.item.amount)">
            {{ getCurrencySignFromPublicId(props.item.currency_public_id) }} {{ formatPrice(Math.abs(props.item.amount)) }}
          </td>
          <td data-label="Status">
            <span :style="getPaymentStatusStyle(props.item.payment_status)">
              {{ (props.item.payment_status || 'N/A').toUpperCase() }}
            </span>
          </td>
          <td data-label="Payment Method">{{ props.item.payment_method || 'N/A' }}</td>
          
          <td data-label="Booking Ref">
            <router-link 
              v-if="props.item.booking_ref_code"
              :to="{ name: 'Booking', params: { booking_id: props.item.booking_id || props.item.booking_ref_code }}"
            >
              <u class="futuristic-link">{{ props.item.booking_ref_code }}</u>
            </router-link>
            <span v-else class="grey--text">-</span>
          </td>
          
          <td data-label="Deposit Ref">
            <span v-if="props.item.deposit_reference" class="deposit-ref">
              {{ props.item.deposit_reference }}
            </span>
            <span v-else class="grey--text">-</span>
          </td>
          
          <td data-label="Description">{{ props.item.description || 'N/A' }}</td>
        </template>
        
        <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">
          Your search for "{{ search }}" found no results.
        </v-alert>
        <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">
          No transactions found for this user.
        </v-alert>
      </v-data-table>
      
      <!-- GET MORE and GET ALL buttons -->
      <v-layout row wrap class="mt-3" v-if="localTransactions.length > 0">
        <v-flex xs12 sm6>
          <v-btn
            v-if="hasMore"
            :depressed="true"
            @click="getMore()"
            color="#587e27"
            :loading="loadingMore"
            class="futuristic-btn"
          >GET MORE</v-btn>
          <v-btn
            v-if="hasMore"
            :depressed="true"
            @click="getAllTransactions()"
            color="#e84118"
            :loading="loadingAll"
            class="futuristic-btn ml-2"
          >GET ALL</v-btn>
        </v-flex>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'UserWalletTransactions',
  
  data: function() {
    return {
      search: '',
      loading: false,
      loadingMore: false,
      loadingAll: false,
      transactionsLoading: false,
      selectedCurrency: 'all',
      currentPage: 2,
      hasMore: true,
      currencies: [
        { public_id: 'all', name: 'All Transactions', sign: '' },
        { public_id: '162fface-f5f1-41de-913b-d2bb784dda3a', name: 'KES', sign: 'KES' },
        { public_id: '6bf74e49-7ff6-4555-a080-15541d62204a', name: 'USD', sign: 'USD' }
      ],
      
      localTransactions: [],
      localBalanceUsd: 0,
      localBalanceKes: 0,
      userInfo: {
        full_name: '',
        email: '',
        phone_number: '',
        public_id: ''
      },
      
      columns: [
        { text: 'Date', value: 'created_at' },
        { text: 'Reference', value: 'reference' },
        { text: 'Type', value: 'transaction_type' },
        { text: 'Amount', value: 'amount' },
        { text: 'Status', value: 'payment_status' },
        { text: 'Payment Method', value: 'payment_method' },
        { text: 'Booking Ref', value: 'booking_ref_code' },
        { text: 'Deposit Ref', value: 'deposit_reference' },
        { text: 'Description', value: 'description' }
      ]
    }
  },
  
  computed: {
    currencyOptions: function() {
      return this.currencies
    },
    
    displayCurrencyPublicId: function() {
      return this.$store.state.display_currency_public_id || '162fface-f5f1-41de-913b-d2bb784dda3a'
    },
    
    displayCurrencySign: function() {
      return this.displayCurrencyPublicId === '162fface-f5f1-41de-913b-d2bb784dda3a' ? 'KES' : 'USD'
    },
    
    displayedBalance: function() {
      if (this.displayCurrencyPublicId === '162fface-f5f1-41de-913b-d2bb784dda3a') {
        return this.localBalanceKes
      } else {
        return this.localBalanceUsd
      }
    },
    
    allTransactions: function() {
      return this.localTransactions
    },
    
    filteredTransactions: function() {
      var self = this
      var filtered = self.allTransactions.slice()
      if (self.selectedCurrency !== 'all') {
        filtered = filtered.filter(function(tx) {
          return tx.currency_public_id === self.selectedCurrency
        })
      }
      return filtered
    }
  },
  
  created: function() {
    this.loadWalletData()
  },
  
  methods: {
    getCurrencySignFromPublicId: function(publicId) {
      if (publicId === '162fface-f5f1-41de-913b-d2bb784dda3a') return 'KES'
      if (publicId === '6bf74e49-7ff6-4555-a080-15541d62204a') return 'USD'
      return 'KES'
    },
    
    loadWalletData: function() {
      var self = this
      var userId = self.$route.params.user_id
      
      if (!userId) {
        return
      }
      
      self.transactionsLoading = true
      self.loading = true
      self.currentPage = 2
      self.hasMore = true
      
      // Load user info
      self.$store.dispatch('get_user', userId)
        .then(function(user) {
          if (user) {
            self.userInfo = {
              full_name: (user.first_name || '') + ' ' + (user.last_name || ''),
              email: user.email || 'No email',
              phone_number: user.phone_number || 'No phone',
              public_id: userId
            }
          }
        })
        .catch(function(err) {
          console.error('Error loading user:', err)
          var userFromStore = self.$store.state.user
          if (userFromStore && userFromStore.email) {
            self.userInfo = {
              full_name: (userFromStore.first_name || '') + ' ' + (userFromStore.last_name || ''),
              email: userFromStore.email || 'No email',
              phone_number: userFromStore.phone_number || 'No phone',
              public_id: userId
            }
          }
        })
      
      // Load wallet balance
      self.$store.dispatch('get_wallet_balance', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localBalanceUsd = res.data.wallet_balance_usd || 0
            self.localBalanceKes = res.data.wallet_balance_kes || 0
          }
          self.loading = false
        })
        .catch(function(err) {
          console.error('Error loading balance:', err)
          self.loading = false
        })
      
      // Load first page of transactions
      self.$store.dispatch('get_wallet_transactions', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localTransactions = res.data || []
            self.hasMore = res.data && res.data.length >= 15
          }
          self.transactionsLoading = false
        })
        .catch(function(err) {
          console.error('Error loading transactions:', err)
          self.transactionsLoading = false
        })
    },
    
    getMore: function() {
      var self = this
      var userId = self.$route.params.user_id
      
      self.loadingMore = true
      
      self.$store.dispatch('get_more_wallet_transactions', {
        userPublicId: userId,
        page: self.currentPage
      })
        .then(function(res) {
          if (res && res.status === 'success') {
            if (res.data && res.data.length > 0) {
              self.localTransactions = [...self.localTransactions, ...res.data]
              self.currentPage++
              self.hasMore = res.data.length >= 15
            } else {
              self.hasMore = false
            }
          }
          self.loadingMore = false
        })
        .catch(function(err) {
          console.error('Error loading more transactions:', err)
          self.loadingMore = false
        })
    },
    
    getAllTransactions: function() {
      var self = this
      var userId = self.$route.params.user_id
      
      self.loadingAll = true
      
      self.$store.dispatch('get_all_wallet_transactions', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localTransactions = res.data || []
            self.hasMore = false
          }
          self.loadingAll = false
        })
        .catch(function(err) {
          console.error('Error loading all transactions:', err)
          self.loadingAll = false
        })
    },
    
    refreshBalance: function() {
      this.localTransactions = []
      this.currentPage = 2
      this.hasMore = true
      this.loadWalletData()
    },
    
    formatPrice: function(price) {
      if (!price && price !== 0) return '0.00'
      return parseFloat(price).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDateTime: function(date) {
      if (!date) return 'N/A'
      if (this.$moment) {
        var parsedDate = this.$moment(date)
        if (parsedDate.isValid()) {
          return parsedDate.format('MMM DD, YYYY HH:mm')
        }
      }
      return new Date(date).toLocaleString()
    },
    
    getTransactionTypeClass: function(type) {
      if (type === 'deposit' || type === 'refund') return 'transaction-positive'
      if (type === 'payment') return 'transaction-negative'
      return 'transaction-neutral'
    },
    
    getAmountClass: function(amount) {
      if (amount > 0) return 'amount-positive'
      if (amount < 0) return 'amount-negative'
      return 'amount-neutral'
    },
    
    getPaymentStatusStyle: function(status) {
      var colors = {
        'completed': '#28a745',
        'pending': '#ffc107',
        'failed': '#dc3545',
        'cancelled': '#6c757d'
      }
      var statusLower = status ? status.toLowerCase() : ''
      var color = colors[statusLower] || '#007bff'
      return 'background-color: ' + color + '; color: #fff; padding: 4px 8px; border-radius: 4px; display: inline-block; font-size: 12px; font-weight: 500;'
    }
  }
}
</script>

<style scoped>
.wallet-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
}
.wallet-container:hover { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1); }
.balance-card { margin: 8px; position: relative; overflow: hidden; transition: all 0.3s ease; }
.balance-card:hover { transform: translateY(-5px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15); }
.unified-card { background: linear-gradient(135deg, #e8f5e9, #ffffff); border-left: 4px solid #587e27; }
.balance-icon { position: absolute; top: 16px; right: 16px; opacity: 0.3; }
.balance-label { font-size: 14px; color: #666; margin-bottom: 8px; font-weight: 500; }
.balance-amount { font-size: 32px; font-weight: 700; color: #2c3e50; margin-bottom: 16px; }
.currency-symbol { font-size: 16px; color: #7f8c8d; margin-left: 4px; }
.user-info { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
.card-header { background: linear-gradient(45deg, #e8eaf6, #f5f7fa); padding: 16px; border-radius: 8px; }
.earth-color { color: #587e27; font-weight: 600; margin: 0; font-size: 1.5rem; }
.title-decoration { height: 4px; background: linear-gradient(90deg, #587e27, transparent); margin-bottom: 16px; width: 100px; }
.futuristic-card { border-radius: 8px; background: #ffffff; transition: all 0.3s ease; margin-top: 8px; }
.futuristic-card:hover { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1); }
.futuristic-input >>> .v-input__slot { border-radius: 8px; background: #f5f7fa; }
.futuristic-search { max-width: 300px; }
.deposit-ref {
  font-family: monospace;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  letter-spacing: 0.5px;
}
.futuristic-link {
  color: #587e27;
  text-decoration: none;
  cursor: pointer;
}
.futuristic-link:hover {
  color: #1b5e20;
  text-decoration: underline;
}
.futuristic-search >>> .v-input__slot { border-radius: 8px; background: #f5f7fa; }
.futuristic-btn { border-radius: 8px; text-transform: none; font-weight: 500; transition: all 0.2s ease; }
.futuristic-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); }
.futuristic-table { border-radius: 8px; background: #ffffff; }
.futuristic-table >>> thead tr th { background: linear-gradient(45deg, #e8eaf6, #f5f7fa); color: #587e27; font-weight: 600; font-size: 13px; }
.futuristic-table >>> tbody tr:hover { background-color: #f5f7fa; }
.transaction-positive { color: #28a745; font-weight: 600; }
.transaction-negative { color: #dc3545; font-weight: 600; }
.transaction-neutral { color: #6c757d; }
.amount-positive { color: #28a745; font-weight: 700; }
.amount-negative { color: #dc3545; font-weight: 700; }
.futuristic-alert { border-radius: 8px; background: #fff5f5; }

@media (max-width: 960px) {
  .wallet-container { padding: 12px; }
  .balance-amount { font-size: 24px; }
  .earth-color { font-size: 1.25rem; }
  .futuristic-btn { font-size: 0.9rem; }
  .futuristic-search { max-width: 200px; }
}
@media (max-width: 600px) {
  .wallet-container { padding: 8px; border-radius: 6px; }
  .balance-card { margin: 4px; }
  .balance-amount { font-size: 20px; }
  .earth-color { font-size: 1.1rem; }
  .futuristic-btn { font-size: 0.85rem; }
  .futuristic-table >>> thead { display: none; }
  .futuristic-table >>> tbody tr td { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-bottom: 1px solid #e0e0e0; }
  .futuristic-table >>> tbody tr td:last-child { border-bottom: none; }
  .futuristic-table >>> tbody tr td::before { content: attr(data-label); font-weight: 600; color: #587e27; margin-right: 16px; min-width: 100px; }
  .futuristic-search { max-width: 100%; margin-top: 8px; }
  .card-header { flex-direction: column; align-items: stretch; }
  .user-info { flex-direction: column; }
}
</style>